import { cloneDeep, get, isEmpty, last } from 'lodash';
import { toHTMLId } from './commonHelper';
import { formatNumber, formatPrice, formatWithDecimal } from '@dmm/lib-common/lib/formatting';
import he from 'he';
import { getConfig } from '../config/portal';
import {
  getCapacity,
  getCapacitySymbol,
  getCurrency,
  getDistance,
  getDistanceSymbol,
  getLength as getLengthUom,
  getLengthSymbol,
  getLocale,
  getSpeed,
  getSpeedSymbol,
  getWeight,
  getWeightSymbol
} from './uomHelper';
import { formatMessage as t, getCurrentCurrency } from './language';
import messages from './translationMessages';
import { SHOW_BOAT_NAME_MIN_LENGTH } from '../constants/index';
import { TITLE_FORMAT, TITLE_DATA } from '../constants/boatDetails/index';
import { getPartyBrandedSRP } from '../utils/urlHelpers/party';

export const getValueTranslation = (group, value)  => {
  const normalizedValue = toHTMLId(value || '');
  if (messages.boatDetails.value[group][normalizedValue]) {
    return t(messages.boatDetails.value[group][normalizedValue]);
  }
  return he.decode(value || '');
};

export const getFriendlyFeatureName = (key) => {
  if (messages.boatDetails.friendlyFeatureName[key]) {
    return t(messages.boatDetails.friendlyFeatureName[key]);
  }
  return he.decode(key || '');
};

export const getTitle = (listing, showBoatName = false, h1Format = TITLE_FORMAT,
  locale = undefined, customUom = undefined) => {
  let title = '';
  const boatLengthMeters = get(listing, 'specifications.dimensions.lengths.nominal.m', 0);
  let boatName = boatLengthMeters >= SHOW_BOAT_NAME_MIN_LENGTH && showBoatName ? get(listing, 'boatName', '') : '';
  boatName = boatName ? ' ' + boatName : boatName;
  const length = getLength(listing, locale, customUom, true);
  const className = (listing.class && messages.classFacetValues[listing.class]) ? t(messages.classFacetValues[listing.class]) : '';
  if (listing.id) {
    title = h1Format;
    title = title.replace('{year}', listing.year);
    title = title.replace('{make}', listing.make);
    title = title.replace('{model}', get(listing, 'model', ''));
    // NOTE: This is weird, I know. But the function literally returns "undefined" when the lenght can't be properly built.
    title = length === 'undefined' ? title : title.replace('{length}', length);
    title = className ? title.replace('{class}', className) : title;
    return title + boatName;
  }
  return title;
};

export const getTitleData = (listing, locale, customUom, titleData = TITLE_DATA) => {
  const length = getLength(listing, locale, customUom, true);
  return titleData.map(element => {
    switch (element) {
    case 'length':
      return {
        facet: 'length',
        data: length,
      };
    case 'year':
      return {
        facet: 'year',
        data: listing.year,
      };
    default:
      return {};
    }
  });
};

const _extractElectronics = (data) => {
  let electronics = [];
  data.map((electronic) => {
    electronics.push(
      {
        title: getFriendlyFeatureName(electronic.code),
        value: (electronic.value) ? electronic.value : '✓'
      });
  });
  return electronics;
};

const _extractEquiptments = (data) => {
  let equipements = [];
  data.map((equipement) => {
    equipements.push(
      {
        title: getFriendlyFeatureName(equipement.code),
        value: (equipement.value) ? equipement.value : '✓'
      });
  });
  return equipements;
};

const _extractRigging = (data) => {
  let riggings = [];
  data.map((rigging) => {
    riggings.push({title: getFriendlyFeatureName(rigging.code), value: (rigging.value) ? rigging.value : '✓'});
  });
  return riggings;
};

const _extractSails = (data) => {
  let sails = [];
  data.map((sail) => {
    sails.push({title: getFriendlyFeatureName(sail.code), value: (sail.value) ? sail.value : '✓'});
  });
  return sails;
};

export const getFeaturesData = (listing) => {
  if (listing.id && listing.features) {
    let features = [];
    let electronicsData = get(listing, 'features.electronics');
    let electricalEquipmentsData = get(listing, 'features.electricalEquipment');
    let insideEquipmentsData = get(listing, 'features.insideEquipment');
    let outsideEquipmentsData = get(listing, 'features.outsideEquipmentExtras');
    let additionalEquipmentsData = get(listing, 'features.additionalEquipment');
    let riggingData = get(listing, 'features.rigging');
    let sailsData = get(listing, 'features.sails');
    if (electricalEquipmentsData) {
      const electricalEquipments = _extractElectronics(electricalEquipmentsData);
      features.push({title: t(messages.boatDetails.features['electrical-equipment']), value: electricalEquipments});
    }

    if (electronicsData) {
      const electronics = _extractElectronics(electronicsData);
      features.push({title: t(messages.boatDetails.features.electronics), value: electronics});
    }

    if (insideEquipmentsData) {
      const insideEquipments = _extractEquiptments(insideEquipmentsData);
      features.push({title: t(messages.boatDetails.features['inside-equipment']), value: insideEquipments});
    }

    if (outsideEquipmentsData) {
      const outsideEquipments = _extractEquiptments(outsideEquipmentsData);
      features.push({title: t(messages.boatDetails.features['outside-equipment']), value: outsideEquipments});
    }

    if (additionalEquipmentsData) {
      const additionalEquipment = _extractEquiptments(additionalEquipmentsData);
      features.push({title: t(messages.boatDetails.features['additional-equipment']), value: additionalEquipment});
    }

    if (riggingData) {
      const rigging = _extractRigging(riggingData);
      features.push({title: t(messages.boatDetails.features.rigging), value: rigging});
    }

    if (sailsData) {
      const sails = _extractSails(sailsData);
      features.push({title: t(messages.boatDetails.features.sails), value: sails});
    }
    return features;
  }
  return [];
};

export const getPrice = (listing, customOptions) => {
  customOptions = !customOptions ? getCurrentCurrency() : customOptions;
  if (customOptions) {
    const shouldShowOriginalCurrency = get(getConfig(), 'supports.showOriginalCurrency', false)
    && get(listing, 'price.enteredCurrency') !== get(customOptions,'currency.code') && !get(listing,'price.hidden');
    const price =  !get(listing, 'price.hidden')
      ? (formatPrice(get(listing, `price.type.amount.${get(customOptions,'currency.code',getCurrency())}`), get(customOptions,'currency.code',getCurrency()), getLocale(), customOptions))
      : t(messages.pricing.request);
    if (shouldShowOriginalCurrency){
      const customFormattingOnly = cloneDeep(customOptions);
      delete customFormattingOnly.currency;
      const originalPrice = formatPrice(get(listing, `price.type.amount.${get(listing, 'price.enteredCurrency')}`), get(listing, 'price.enteredCurrency'), getLocale(), customFormattingOnly);
      return `${originalPrice} (${price})`;
    }
    return price;
  }
  return !get(listing, 'price.hidden')
    ? (formatPrice(get(listing, `price.type.amount.${getCurrency()}`), getCurrency(), getLocale()))
    : t(messages.pricing.request);
};

export const getLength = (listing, locale, customUom, roundValue = false) => {
  const uom = customUom ? customUom.abbr : getLengthUom();
  const symbol = customUom ? customUom.symbol : getLengthSymbol();
  const length = roundValue ? Math.round(get(listing, `specifications.dimensions.lengths.nominal.${uom}`)) :
    get(listing, `specifications.dimensions.lengths.nominal.${uom}`);
  return formatWithDecimal(
    length,
    locale ? locale : getLocale(),
    2,
    null,
    true)
    + symbol;
};

export const floorLength = (length) => {
  if (length > 999) {
    return 999;
  }
  return Math.floor(length);
};

export const getHullParam = (value) => {
  return value.split('-').join(' ');
};

export const getLocation = (listing) => {
  const { city, subdivision, country } = get(listing, 'location.address', {});
  const fullSubdivision = messages.countrySubdivision[country] &&
  messages.countrySubdivision[country][subdivision]
    ? t(messages.countrySubdivision[country][subdivision])
    : subdivision;

  const fullCountry = messages.countries[country] ? t(messages.countries[country]) : country;
  if (city) {
    return city + ', ' + (fullSubdivision || fullCountry);
  }
  if (subdivision) {
    return fullSubdivision;
  }
  return fullCountry;
};

export const getSubdivision = (listing) => {
  const { country, subdivision } = get(listing, 'location.address', {});
  const fullSubdivision = messages.countrySubdivision[country] &&
  messages.countrySubdivision[country][subdivision]
    ? t(messages.countrySubdivision[country][subdivision])
    : subdivision;

  if (subdivision) {
    return fullSubdivision;
  }
};

export const getWarrantyLabel = (listing) => {
  const warranty = get(listing, 'legal.hullWarranty');
  if (!warranty) {return;}
  const isMonth = last(warranty) === 'm';
  if (isMonth) {
    return warranty.replace('m', ` ${t(messages.boatDetails.warranty.months)}`);
  }
  const years = Number(warranty);
  if (years === 1) {
    return t(messages.boatDetails.warranty.oneYear, { years });
  }
  if (years === 99) {
    return t(messages.boatDetails.warranty.lifetime);
  }
  if (years > 0) {
    return t(messages.boatDetails.warranty.years, { years });
  }
};

export const getDetailsData = (listing, { length: customLength } = {}) => {
  let details = [];
  if (listing.year) {
    details.push({title: t(messages.boatDetails.details.year), value: listing.year});
  }
  if (listing.make) {
    details.push({title: t(messages.boatDetails.details.make), value: listing.make});
  }
  if (listing.model) {
    details.push({title: t(messages.boatDetails.details.model), value: listing.model});
  }
  if (listing.class) {
    details.push({title: t(messages.boatDetails.details.class), value: t(messages.classFacetValues[listing.class])});
  }
  if (
    listing.specifications &&
    get(listing, `specifications.dimensions.lengths.nominal.${getLengthUom(customLength)}`)
  ) {
    let length = getLength(listing, undefined, customLength);
    details.push({title: t(messages.boatDetails.details.length), value: length});
  }
  if (listing.fuelType) {
    details.push({
      title: t(messages.boatDetails.details['fuel-type']),
      value: getValueTranslation('fuelType', getHullParam(listing.fuelType)),
    });
  }
  if (listing.hull) {
    if (listing.hull.material) {
      details.push({
        title: t(messages.boatDetails.details['hull-material']),
        value: getValueTranslation('hullMaterial', getHullParam(listing.hull.material)),
      });
    }
    if (listing.hull.shape) {
      details.push({
        title: t(messages.boatDetails.details['hull-shape']),
        value: getValueTranslation('hullShape', getHullParam(listing.hull.shape)),
      });
    }
    if (listing.hull.hin && get(getConfig(), 'supports.hideHin', false) !== true) {
      details.push({title: t(messages.boatDetails.details.hin), value: listing.hull.hin});
    }
  }
  const warrantyLabel = getWarrantyLabel(listing);
  if (warrantyLabel) {
    details.push({title: t(messages.boatDetails.details['hull-warranty']), value: warrantyLabel});
  }
  return details;
};

export const getPropulsionData = (listing) => {
  let details = [];
  if (listing.propulsion && !isEmpty(listing.propulsion.engines)) {
    if (listing.propulsion.engines.length > 1) {
      details = listing.propulsion.engines.map( (engine, index) => {
        return {
          title: t(messages.boatDetails.propulsion.engine, { number: index + 1 }),
          value: _extractPropulsionData(engine),
        };
      });
    } else {
      details = _extractPropulsionData(listing.propulsion.engines[0]);
    }
  }
  return details;
};

export const getEnginePower = (engine) => {
  let engineDetails = {
    title: '',
    value: ''
  };
  if (engine.power.hp) {
    engineDetails = {title: t(messages.boatDetails.propulsion['total-power']), value: t(messages.boatDetails.propulsion.power.hp, { power: engine.power.hp })};
  } else if (engine.power.kW) {
    engineDetails = {title: t(messages.boatDetails.propulsion['total-power']), value: t(messages.boatDetails.propulsion.power.kW, { power: engine.power.kW })};
  }
  return engineDetails;
};

const _extractPropulsionData = (engine) => {
  let engineDetails = [];
  if (engine.make) {
    engineDetails.push({title: t(messages.boatDetails.propulsion['engine-make']), value: `${engine.make}`});
  }
  if (engine.model) {
    engineDetails.push({title: t(messages.boatDetails.propulsion['engine-model']), value: `${engine.model}`});
  }
  if (engine.year) {
    engineDetails.push({title: t(messages.boatDetails.propulsion['engine-year']), value: `${engine.year}`});
  }
  if (engine.power) {
    if (engine.power.hp) {
      engineDetails.push({title: t(messages.boatDetails.propulsion['total-power']), value: t(messages.boatDetails.propulsion.power.hp, { power: engine.power.hp })});
    } else if  (engine.power.kW) {
      engineDetails.push({title: t(messages.boatDetails.propulsion['total-power']), value: t(messages.boatDetails.propulsion.power.kW, { power: engine.power.kW })});
    }
  }
  if (engine.hours) {
    engineDetails.push({title: t(messages.boatDetails.propulsion['engine-hours']), value: `${engine.hours}`});
  }
  if (engine.category) {
    engineDetails.push({
      title: t(messages.boatDetails.propulsion['engine-type']),
      value: getValueTranslation('engineType', engine.category),
    });
  }
  if (engine.driveType) {
    engineDetails.push({
      title: t(messages.boatDetails.propulsion['drive-type']),
      value: getValueTranslation('driveType', engine.driveType),
    });
  }
  if (engine.location) {
    engineDetails.push({
      title: t(messages.boatDetails.propulsion.location),
      value: getValueTranslation('engineLocation', engine.location),
    });
  }
  if (engine.fuel) {
    engineDetails.push({
      title: t(messages.boatDetails.propulsion['fuel-type']),
      value: getValueTranslation('fuelType', engine.fuel),
    });
  }
  if (engine.ropeCutter) {
    engineDetails.push({title: t(messages.boatDetails.propulsion['rope-cutter']), value: true});
  }
  if (engine.propellerType) {
    engineDetails.push({
      title: t(messages.boatDetails.propulsion['propeller-type']),
      value: getValueTranslation('propellerType', engine.propellerType),
    });
  }
  if (engine.propellerMaterial) {
    engineDetails.push({
      title: t(messages.boatDetails.propulsion['propeller-material']),
      value: getValueTranslation('propellerMaterial', engine.propellerMaterial),
    });
  }
  if (engine.foldingPropeller) {
    engineDetails.push({title: t(messages.boatDetails.propulsion['folding-propeller']), value: true});
  }
  return engineDetails;
};

const _buildFieldTitle = (property) => {
  const translationKey = get(messages.boatDetails.measurements, property);
  if (translationKey) {
    return t(translationKey);
  }
  const upper = property.charAt(0).toUpperCase();
  const buildedTitle = upper + property.slice(1);
  return buildedTitle;
};

// The following function should be considered as a temporary function to iterate over an object and get all
// existing fields. Why? Because there are many translations that do not exist at the moment,
// so this will change the way we handle details, descriptions and measurements when those translations
// are added
const _getAvailableFields = (section) => {
  let listOfAvailableProperties = [];
  let fieldTitle;
  let sectionObject = {...section};
  delete sectionObject.weight;
  for (const property in sectionObject) {
    fieldTitle = _buildFieldTitle(property).replace(/([a-z])([A-Z])/g, '$1 $2');
    if (typeof sectionObject[property] === 'object' && sectionObject[property] !== null) {
      const unit = Object.keys(sectionObject[property])[0];
      const value = Object.values(sectionObject[property])[0];
      const applicableValue = `${value}${unit}`;
      listOfAvailableProperties.push({
        title: fieldTitle,
        value: applicableValue
      });
    } else if (sectionObject[property]) {
      const applicableValue = sectionObject[property];
      listOfAvailableProperties.push({
        title: fieldTitle,
        value: applicableValue
      });
    }
  }
  return listOfAvailableProperties;
};

export const getEngineDetailsData = (engine) => {
  let engineDetailsData = [];

  if (engine.make) {
    engineDetailsData.push({title: t(messages.boatDetails.details.make), value: engine.make});
  }
  if (engine.model) {
    engineDetailsData.push({title: t(messages.boatDetails.details.model), value: engine.model});
  }
  if (engine.year) {
    engineDetailsData.push({title: t(messages.boatDetails.details.year), value: engine.year});
  }
  if (engine.power) {
    if (engine.power.hp) {
      engineDetailsData.push({title: t(messages.boatDetails.propulsion['total-power']), value: t(messages.boatDetails.propulsion.power.hp, { power: engine.power.hp })});
    } else if  (engine.power.kW) {
      engineDetailsData.push({title: t(messages.boatDetails.propulsion['total-power']), value: t(messages.boatDetails.propulsion.power.kW, { power: engine.power.kW })});
    }
  }
  let address = get(engine, 'location.address');
  if (address) {
    const location = getLocation(engine);
    engineDetailsData.push({title: t(messages.boatDetails.propulsion.location), value: location});
  }
  if (engine.hours) {
    engineDetailsData.push({
      title: t(messages.boatDetails.propulsion['engine-hours']),
      value: engine.hours});
  }
  if (engine.fuel) {
    engineDetailsData.push({
      title: t(messages.boatDetails.propulsion['fuel-type']),
      value: t(messages.boatDetails.value.fuelType[engine.fuel]),
    });
  }
  if (engine.condition) {
    engineDetailsData.push({title: t(messages.condition), value: t(messages[engine.condition])});
  }
  // Translations needed
  if (engine.configuration) {
    engineDetailsData.push({
      title: t(messages.boatDetails.propulsion.configuration),
      value: getValueTranslation('configuration', engine.configuration)
    });
  }
  if (engine.use) {
    engineDetailsData.push({
      title: t(messages.boatDetails.propulsion.use),
      value: getValueTranslation('use', engine.use)
    });
  }
  if (engine.numberCylinders) {
    engineDetailsData.push({title: t(messages.boatDetails.propulsion.numberCylinders), value: engine.numberCylinders});
  }
  if (engine.stockNumber) {
    engineDetailsData.push({title: t(messages.boatDetails.propulsion.stockNumber), value: engine.stockNumber});
  }
  if (engine.serialNumber) {
    engineDetailsData.push({title: t(messages.boatDetails.propulsion.serialNumber), value: engine.serialNumber});
  }

  return engineDetailsData;
};

export const getEngineMeasurementsData = (listing) => {
  let details = [];

  const measurements = get(listing, 'measurements', {});
  details = _getAvailableFields(measurements);
  if (measurements.weight) {
    let weight = _extractWeights(measurements.weight);
    details.push({title: t(messages.boatDetails.measurements.weights), value: weight.value});
  }
  return details;
};

export const getMeasurementsData = (listing, customUom = {}) => {
  let details = [];

  const specs = get(listing, 'specifications', {});
  const equipment = get(listing, 'features.equipment', {});

  if (specs.speedDistance) {
    let speed = _extractSpeedAndDistanceData(specs.speedDistance, customUom);
    if (speed.length !== 0) {
      details.push({title: t(messages.boatDetails.measurements['speed-distance']), value: speed});
    }
  }
  if (specs.dimensions) {
    let dim = _extractDimensions(specs.dimensions, customUom);
    if (dim.length !== 0) {
      details.push({title: t(messages.boatDetails.measurements.dimensions), value: dim});
    }
  }
  if (specs.weights) {
    let weights = _extractWeights(specs.weights, customUom);
    if (weights.length !== 0) {
      details.push({title: t(messages.boatDetails.measurements.weights), value: weights});
    }
  }
  let misc = _extractMiscDimensions(specs,equipment);
  if (misc.length !== 0) {
    details.push({title: t(messages.boatDetails.measurements.miscellaneous), value: misc});
  }

  if (listing.tanks) {
    let tanks = _extractTanks(listing.tanks, customUom);
    if (tanks.length !== 0) {
      details.push({title: t(messages.boatDetails.measurements.tanks), value: tanks});
    }
  }

  if (specs.accommodation) {
    let accom = _extractAccomodations(specs.accommodation);
    if (accom.length !== 0) {
      details.push({title: t(messages.boatDetails.measurements.accommodations), value: accom});
    }
  }
  return details;
};

const _extractSpeedAndDistanceData = (data, customUom) => {
  const customSpeed = get(customUom, 'speed');
  const customDistance = get(customUom, 'distance');
  let speedSymbol = customSpeed ? customSpeed.symbol : getSpeedSymbol();
  let speedAbbr = customSpeed ? customSpeed.abbr : getSpeed();

  let speed = [];
  if (data.cruisingSpeed) {
    speed.push({
      title: t(messages.boatDetails.measurements['cruising-speed']),
      value: data.cruisingSpeed[speedAbbr] + speedSymbol,
    });
  }
  if (data.maxHullSpeed) {
    speed.push({
      title: t(messages.boatDetails.measurements['max-speed']),
      value: data.maxHullSpeed[speedAbbr] + speedSymbol,
    });
  }
  if (data.range) {
    const distanceSymbol = customDistance ? customDistance.symbol : getDistanceSymbol();
    const distanceAbbr = customDistance ? customDistance.abbr : getDistance();
    speed.push({
      title: t(messages.boatDetails.measurements.range),
      value: data.range[distanceAbbr] + distanceSymbol,
    });
  }
  return speed;
};

const _extractDimensions = (data, customUom) => {
  const customLengthUom = get(customUom, 'length');
  const lengthSymbol = customLengthUom ? customLengthUom.symbol : getLengthSymbol();
  const lengthMetric = customLengthUom ? customLengthUom.abbr : getLengthUom();
  let dimensions = [];
  const overall = get(data, `lengths.overall.${lengthMetric}`);
  const deck = get(data, `lengths.deck.${lengthMetric}`);
  const waterline = get(data, 'lengths.waterline');
  if (overall) {
    dimensions.push({
      title: t(messages.boatDetails.measurements['length-overall']),
      value: overall + lengthSymbol,
    });
  }
  if (deck) {
    dimensions.push({
      title: t(messages.boatDetails.measurements['length-on-deck']),
      value: deck + lengthSymbol,
    });
  }
  if (data.maxBridgeClearance) {
    dimensions.push({
      title: t(messages.boatDetails.measurements['max-bridge-clearance']),
      value: data.maxBridgeClearance[lengthMetric] + lengthSymbol,
    });
  }
  if (data.maxDraft) {
    dimensions.push({
      title: t(messages.boatDetails.measurements['max-draft']),
      value: data.maxDraft[lengthMetric] + lengthSymbol,
    });
  }
  if (data.freeboard) {
    dimensions.push({
      title: t(messages.boatDetails.measurements.freeboard),
      value: data.freeboard[lengthMetric] + lengthSymbol,
    });
  }
  if (data.beam) {
    dimensions.push({
      title: t(messages.boatDetails.measurements.beam),
      value: data.beam[lengthMetric] + lengthSymbol,
    });
  }
  if (data.cabinHeadroom) {
    dimensions.push({
      title: t(messages.boatDetails.measurements['cabin-headroom']),
      value: data.cabinHeadroom[lengthMetric] + lengthSymbol,
    });
  }
  if (waterline) {
    dimensions.push({
      title: t(messages.boatDetails.measurements['length-at-waterline']),
      value: waterline[lengthMetric] + lengthSymbol,
    });
  }
  return dimensions;
};

const _extractWeights = (weights, { weight: customWeight } = {}) => {
  const weightSymbol = customWeight ? customWeight.symbol : getWeightSymbol();
  const weightMetric = customWeight ? customWeight.abbr : getWeight();
  let weight = [];
  if (weights.dry || weights.type === 'dry') {
    const amount = weights.dry ? weights.dry[weightMetric] : weights[weightMetric];
    let formattedDry = formatNumber(amount, getLocale());
    weights.type === 'dry' ?
      weight = { title: t(messages.boatDetails.measurements['dry-weight']), value: formattedDry + weightSymbol} :
      weight.push({ title: t(messages.boatDetails.measurements['dry-weight']), value: formattedDry + weightSymbol});
  }
  return weight;
};

const _extractMiscDimensions = (specs,equipment) =>{
  let miscellaneous = [];

  const seats = get(specs, 'capacity.seatingCapacity');
  const windlass = get(equipment, 'windlass');
  const liferaft = get(specs, 'capacity.liferaftCapacity');
  const deadrise = get(specs, 'dimensions.deadriseAtTransom');
  const circuit = get(equipment, 'electricalCircuit');
  if (seats) {
    miscellaneous.push({
      title: t(messages.boatDetails.measurements['seating-capacity']),
      value: seats,
    });
  }
  if (windlass) {
    miscellaneous.push({
      title: t(messages.boatDetails.measurements.windlass),
      value: getValueTranslation('windlass', windlass),
    });
  }
  if (liferaft) {
    miscellaneous.push({
      title: t(messages.boatDetails.measurements['liferaft-capacity']),
      value: liferaft,
    });
  }
  if (deadrise) {
    miscellaneous.push({
      title: t(messages.boatDetails.measurements['deadrise-at-transom']),
      value: `${deadrise.value}${deadrise.unit}`,
    });
  }
  if (circuit) {
    miscellaneous.push({
      title: t(messages.boatDetails.measurements['electrical-circuit']),
      value: getValueTranslation('electricalCircuit', circuit),
    });
  }
  return miscellaneous;
};

export const formatMoreDetailsEngineDescriptions = (listing) => {
  let moreDetails = [];

  if (listing.rpmMax) {
    moreDetails.push({
      title: t(messages.boatDetails.propulsion.rpmMax),
      value: `${listing.rpmMax}rpm`,
    });
  }
  if (listing.fuelInductionSystem) {
    moreDetails.push({
      title: t(messages.boatDetails.propulsion.fuelInductionSystem),
      value: listing.fuelInductionSystem,
    });
  }
  if (listing.startingSystem) {
    moreDetails.push({
      title: t(messages.boatDetails.propulsion.startingSystem),
      value: listing.startingSystem,
    });
  }
  if (listing.ignitionSystem) {
    moreDetails.push({
      title: t(messages.boatDetails.propulsion.ignitionSystem),
      value: listing.ignitionSystem,
    });
  }
  if (listing.lubricationSystem) {
    moreDetails.push({
      title: t(messages.boatDetails.propulsion.lubricationSystem),
      value: listing.lubricationSystem,
    });
  }
  if (listing.steeringSystem) {
    moreDetails.push({
      title: t(messages.boatDetails.propulsion.steeringSystem),
      value: listing.steeringSystem,
    });
  }
  if (listing.coolingSystem) {
    moreDetails.push({
      title: t(messages.boatDetails.propulsion.coolingSystem),
      value: listing.coolingSystem,
    });
  }
  if (listing.emissions) {
    moreDetails.push({
      title: t(messages.boatDetails.propulsion.emissions),
      value: listing.emissions,
    });
  }
  if (listing.propellerType) {
    moreDetails.push({
      title: t(messages.boatDetails.propulsion['propeller-type']),
      value: listing.propellerType,
    });
  }
  return moreDetails;
};

export const formatMoreDetailsDescriptions = (descriptions) => {
  if (descriptions) {
    return descriptions.filter(desc => desc.visibility === 'public').map(description => {
      return {
        title: getValueTranslation('moreDetailsSection', description.title),
        value: description.description,
      };
    });
  }
};

const _extractTanks = (tank, customUom) => {
  const customCapacity = get(customUom, 'capacity');
  let tanks = [];

  const freshWaterTanks = get(tank, 'freshWater');
  const fuelTanks = get(tank, 'fuel');
  const holdingTanks = get(tank, 'holding');

  const getTanksValue = tanks => tanks
    .map((tank) => {
      const capacityUnit = customCapacity ? customCapacity.abbr : getCapacity();
      const capacitySymbol = customCapacity ? customCapacity.symbol : getCapacitySymbol();
      const capacity = get(tank, `capacity[${capacityUnit}]`)
        ? `${tank.capacity[capacityUnit]} ${capacitySymbol} ` : '';
      const material = getValueTranslation('tankMaterial', get(tank, 'tankMaterial', ''));
      const quantity = get(tank, 'quantity') ? `${tank.quantity} x ` : '';

      return `${quantity}${capacity}(${material})`;
    })
    .join(', ');

  if (freshWaterTanks) {
    tanks.push({
      title: t(messages.boatDetails.measurements['fresh-water-tank']),
      value: getTanksValue(freshWaterTanks),
    });
  }
  if (fuelTanks) {
    tanks.push({
      title: t(messages.boatDetails.measurements['fuel-tank']),
      value: getTanksValue(fuelTanks),
    });
  }
  if (holdingTanks) {
    tanks.push({
      title: t(messages.boatDetails.measurements['holding-tank']),
      value: getTanksValue(holdingTanks),
    });
  }

  return tanks;
};

const _extractAccomodations = (accommodation) => {
  let accommodations = [];
  if (accommodation.singleBerths) {
    accommodations.push({
      title: t(messages.boatDetails.measurements['single-berths']),
      value: accommodation.singleBerths,
    });
  }
  if (accommodation.doubleBerths) {
    accommodations.push({
      title: t(messages.boatDetails.measurements['double-berths']),
      value: accommodation.doubleBerths,
    });
  }
  if (accommodation.twinBerths) {
    accommodations.push({
      title: t(messages.boatDetails.measurements['twin-berths']),
      value: accommodation.twinBerths,
    });
  }
  if (accommodation.cabins) {
    accommodations.push({
      title: t(messages.boatDetails.measurements.cabins),
      value: accommodation.cabins,
    });
  }
  if (accommodation.heads) {
    accommodations.push({
      title: t(messages.boatDetails.measurements.heads),
      value: accommodation.heads,
    });
  }
  return accommodations;
};

export const getPrePopulatedText = (listing) => {
  return `I'm interested in getting more information about your ${getTitle(listing)}. Please contact me.`;
};

export const isFSBOContact = (contact) => {
  return get(contact, 'name') === 'Private Seller';
};

export const getSanitisedDealerName = (owner) => {
  let dealerName = '';
  let name = owner.name;
  if (owner.rootName && !name.includes(owner.rootName)) {
    dealerName = owner.rootName + ' - ';
  }
  dealerName += name;
  return dealerName;
};

const _stripQueryParams = (url) => {
  return url.split('?')[0];
};

export const addResizeParams = (url, resize, modified, exact = true) => {
  if (resize.width) {
    let timestamp = (modified ? new Date(modified) : new Date()).getTime();
    return  _stripQueryParams(url) + `?w=${resize.width}&h=${resize.height}&t=${timestamp}${exact ? '&exact' : ''}`;
  }
  return url;
};

export const getMediaImages = (listing) => {
  const listingMedia = get(listing, 'media', []);
  return listingMedia.filter((item) => {
    return item.mediaType === 'image';
  });
};

export const get360Photos = (listing) => {
  const listingMedia = get(listing, 'media', []);
  return listingMedia.filter((item) => {
    return item.mediaType === '360Image';
  });
};

export const getMediaVideos = (listing) => {
  const listingMedia = get(listing, 'media', []);
  return listingMedia.filter((item) => {
    return _isYouTubeVideo(item) || item.mediaType === 'video';
  });
};

const _isYouTubeVideo = (item) => {
  if (item.mediaType === 'external') {
    let videoUrl = get(item, 'videoEmbedUrl') || '';
    var regExp = /(?:https?:\/\/)?(?:www\.)?youtu\.?be(?:\.com)?\/?.*(?:watch|embed)?(?:.*v=|v\/|\/)([\w-_]+)/;
    return videoUrl.match(regExp);
  }
};

export const getSellerName = (sellerContact = {}, preferredName = '') => {
  if (isFSBOContact(sellerContact)) {
    return t(messages.fsbo);
  }

  return preferredName || sellerContact.rootName || sellerContact.name || '';
};

export const isAvailableForLiveVideoTour = listing =>
  get(listing, 'attributes', []).includes('LIVE_VIDEO_TOUR');

export const getBrokerSpotlightInfo = (party, spotlightDetails, openContactFormFunc, openListingsFunc) => {
  if (!spotlightDetails || !party){
    return null;
  }
  const salesRep = spotlightDetails.details ?
    {normalizedName: spotlightDetails.details.normalizedBrokerName, id: spotlightDetails.id} : null;
  const spotlightInfo = {
    brokerCardMode: 'contact',
    brokerImageUrl: get(spotlightDetails,'details.brokerImageUrl',null),
    brokerageLogoUrl: get(spotlightDetails, 'details.brokerageLogoUrl',null),
    brokerageUrl: `${getPartyBrandedSRP(party, get(getConfig(), 'supports.customBrandedSRP', false), false, null)}`,
    brokerName: get(spotlightDetails, 'details.brokerName', ''),
    parentName: get(spotlightDetails, 'details.parentName', ''),
    contactButtonText: t(messages.contactGroup.contact),
    listingsButtonText: t(messages.brokerListings),
    brokerListingsUrl: `${getPartyBrandedSRP(party, get(getConfig(), 'supports.customBrandedSRP', false), false, null, salesRep)}`,
    brokerListings: [],
    contactButtonClick: openContactFormFunc,
    listingsButtonClick: openListingsFunc,
    premiumBroker: null //this will need to be changed once the premiumBroker info exists.
  };
  return spotlightInfo;
};
